/**
 * THE FLEET CALENDAR: EVERY RV DOWN THE SIDE, THE NEXT THIRTY DAYS ACROSS.
 *
 * Anthony, 2026-09-28, for WeRV. The question on the phone is "have you
 * got a Class C for the 10th to the 14th", and the answer is a row of
 * squares, not a list of trips.
 *
 * A trip is a run of coloured squares from pickup to the night before the
 * return. The return day and the day before pickup are shaded as
 * turnover, because that is exactly what migration 0185's constraint
 * holds (blocksOf in src/domain/rental.ts draws them, so the shading and
 * the refusal cannot disagree). An inquiry or a quote is drawn pale and
 * holds nothing: two families can be quoted the same weekend.
 *
 * TAP AN EMPTY SQUARE to start a new trip for that RV on that day. Tap a
 * trip to open it. The table scrolls sideways on a phone with the RV
 * names held at the left edge.
 */

import { esc, mount } from "../ui/dom.ts";
import { rentalsApi } from "../data/rentals.ts";
import { rentalsDown } from "./rentals.ts";
import { addDays, blocksOf, holds, unitName, type Stage, type Unit } from "../domain/rental.ts";

type CalTrip = { id: string; vehicle_id: string; stage: Stage; start_date: string; end_date: string; renter_name: string | null; delivery: boolean };
type CalAnswer = { today: string; from: string; days: number; may_write: boolean; units: Unit[]; trips: CalTrip[] };

let offset = 0;

export async function rentalCalendarPage(): Promise<void> {
  mount(`<div class="page page-wide">
    <header class="page-head"><h1>Fleet Calendar</h1>
      <p>Tap an empty day to start a trip for that RV. Shaded days are turnover, one each side of a trip.</p></header>
    <div class="rhead-acts"><button class="btn sm" id="rc-back">Earlier</button><button class="btn sm" id="rc-today">Today</button>
      <button class="btn sm" id="rc-next">Later</button><a class="btn primary sm" href="#/newtrip">New trip</a></div>
    <div class="card" id="rc-host"><div class="loading">Reading&hellip;</div></div>
    <p class="quiet rkey"><span><i class="rk rk-hold"></i>Booked or out</span> <span><i class="rk rk-soft"></i>Inquiry or quoted, holds nothing</span>
      <span><i class="rk rk-turn"></i>Turnover</span></p>
  </div>`);
  document.getElementById("rc-back")?.addEventListener("click", () => { offset -= 14; void draw(); });
  document.getElementById("rc-next")?.addEventListener("click", () => { offset += 14; void draw(); });
  document.getElementById("rc-today")?.addEventListener("click", () => { offset = 0; void draw(); });
  await draw();
}

async function draw(): Promise<void> {
  const host = document.getElementById("rc-host");
  if (!host) return;
  const first = await rentalsApi<CalAnswer>("/calendar?days=30");
  if (!first.ok) { host.innerHTML = rentalsDown(first.error, first.missing); return; }
  let data = first.data;
  if (offset) {
    const moved = await rentalsApi<CalAnswer>(`/calendar?days=30&from=${addDays(first.data.today, offset)}`);
    if (!moved.ok) { host.innerHTML = rentalsDown(moved.error, moved.missing); return; }
    data = moved.data;
  }
  const days = Array.from({ length: data.days }, (_, i) => addDays(data.from, i));
  if (!data.units.length) {
    host.innerHTML = `<div class="empty">No RV has a nightly rate yet. Open one from Fleet and fill in its Rentals card.</div>`;
    return;
  }
  const head = days.map((d) => {
    const dt = new Date(`${d}T12:00:00Z`);
    const wk = dt.toLocaleDateString("en-US", { weekday: "narrow", timeZone: "UTC" });
    return `<th class="${d === data.today ? "rc-today" : ""}" scope="col"><span>${esc(wk)}</span>${dt.getUTCDate()}</th>`;
  }).join("");

  const rows = data.units.map((u) => {
    const mine = data.trips.filter((t) => t.vehicle_id === u.id);
    const cells = days.map((d) => {
      const on = mine.find((t) => d >= t.start_date && d < t.end_date);
      if (on) {
        const firstDay = d === on.start_date || d === data.from;
        return `<td class="rc-trip ${holds(on.stage) ? "rc-hold" : "rc-soft"}"><a href="#/trip/${esc(on.id)}" title="${
          esc(on.renter_name ?? "")}">${firstDay ? esc((on.renter_name ?? "Trip").split(" ")[0]) : "&nbsp;"}</a></td>`;
      }
      const turn = mine.some((t) => holds(t.stage) && (() => { const b = blocksOf(t.start_date, t.end_date); return d >= b.from && d < b.to; })());
      if (turn) return `<td class="rc-turn" title="Turnover"></td>`;
      return data.may_write && d >= data.today
        ? `<td class="rc-open"><a href="#/newtrip/${esc(u.id)}/${d}" aria-label="New trip on ${esc(unitName(u))} from ${d}"></a></td>`
        : `<td></td>`;
    }).join("");
    return `<tr><th scope="row" class="rc-unit"><a href="#/vehicle/${esc(u.id)}">${esc(unitName(u))}</a>${
      u.trim ? `<i>${esc(u.trim)}</i>` : ""}</th>${cells}</tr>`;
  }).join("");
  host.innerHTML = `<div class="rc-scroll"><table class="rcal"><thead><tr><th class="rc-unit" scope="col">RV</th>${head}</tr></thead>
    <tbody>${rows}</tbody></table></div>`;
}
